import {
  Avatar,
  Box,
  ExpandIcon as ExpandMoreIcon,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  Theme,
  Tooltip,
  Typography,
  makeStyles,
} from '@forgeglobal/design-framework'
import { ExpansionTableRowProps, ExpansionTableStyles, StyleDefaults } from 'isolated-design-library'
import { OrderBookCompanyFragment } from 'graphql/bindings/AdminBindings'
import { OrderBookExpandedContent } from 'components/order-book/OrderBookExpandedContent'
import { formatAbbreviatedMoney } from 'util/NumberUtils'
import { localShortDate } from 'util/DateUtils'
import React from 'react'

const useStyles = makeStyles((theme: Theme) => ({
  fullWidth: {
    width: '100%',
  },
  shorterRow: {
    height: theme.spacing(8),
    padding: theme.spacing(2),
  },
  avatarContainer: {
    display: 'inline-flex',
    verticalAlign: 'middle',
    alignItems: 'center',
  },
  avatarName: {
    marginTop: -2,
    marginLeft: theme.spacing(1),
  },
  details: {
    padding: theme.spacing(2),
  },
}))

export const OrderBookRow: React.FC<ExpansionTableRowProps<OrderBookCompanyFragment>> = props => {
  const { style, columnDefs, data, expand, expanded } = props
  const lastRoundDate = data?.last_round?.round_date
  const lastRoundValuation = data?.last_round?.valuation ?? 0

  const tableStyle = ExpansionTableStyles(columnDefs)
  const classes = useStyles()

  const bidColor = StyleDefaults.palette.bid
  const askColor = StyleDefaults.palette.ask

  return (
    <Box pb={1} style={style}>
      <ExpansionPanel expanded={expanded} onChange={expand}>
        <ExpansionPanelSummary className={classes.shorterRow} expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" className={classes.fullWidth}>
            <div className={tableStyle.colSpec}>
              <span key={'issuerName'} className={classes.avatarContainer}>
                <Avatar src={data.logo_url} />
                <span className={classes.avatarName}>{data.name}</span>
              </span>
              <Tooltip key={'valuation'} title={!!lastRoundDate && localShortDate(lastRoundDate)}>
                <span>{!!lastRoundValuation && formatAbbreviatedMoney(lastRoundValuation)}</span>
              </Tooltip>
              <span key={'bidVol'} style={{ color: bidColor }}>
                {formatAbbreviatedMoney(data.bid_ask_summary.bid_volume)}
              </span>
              <span key={'bidActionable'} style={{ color: bidColor }}>
                {formatAbbreviatedMoney(data.bid_ask_summary.bid_actionable)}
              </span>
              <span key={'bidStructures'} style={{ color: bidColor }}>
                {data.bid_ask_summary.bid_structures}
              </span>
              <span key={'askVol'} style={{ color: askColor }}>
                {formatAbbreviatedMoney(data.bid_ask_summary.ask_volume)}
              </span>
              <span key={'askActionable'} style={{ color: askColor }}>
                {formatAbbreviatedMoney(data.bid_ask_summary.ask_actionable)}
              </span>
              <span key={'askStructures'} style={{ color: askColor }}>
                {data.bid_ask_summary.ask_structures}
              </span>
            </div>
          </Typography>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails className={classes.details}>
          {expanded && <OrderBookExpandedContent companyId={data.id} />}
        </ExpansionPanelDetails>
      </ExpansionPanel>
    </Box>
  )
}
